import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { postsApi, metaApi } from '../services/api';
import { Link } from 'react-router-dom';
import LoadingOverlay from '../components/LoadingOverlay';

export default function HomePage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState<any[]>([]);
  const [metaStatus, setMetaStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      postsApi.list().then(r => setPosts(r.data.data || [])).catch(() => {}),
      metaApi.status().then(r => setMetaStatus(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const socialConnected = !!(metaStatus?.connected || user?.has_facebook || user?.has_instagram);
  const hasPlan = user?.subscription_status === 'active' || user?.subscription_status === 'paused';
  const isSetupComplete = !!(socialConnected && hasPlan);
  const planActive = ['active', 'paused', 'canceling'].includes(user?.subscription_status ?? '');

  const firstName = user?.name?.split(' ')[0] || '';
  const initials = user?.name?.split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase() || '?';

  const planLabel = user?.subscription_plan
    ? user.subscription_plan.charAt(0).toUpperCase() + user.subscription_plan.slice(1)
    : '—';

  const statusLabel = (s: string) => {
    if (s === 'published') return 'Pubblicato';
    if (s === 'publishing') return 'In pubblicazione';
    if (s === 'failed') return 'Fallito';
    return s;
  };

  return (
    <div className="page">
      {loading && <LoadingOverlay title="Caricamento..." subtitle="Un momento" icon="home" />}
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ fontSize: 20, marginBottom: 2 }}>Ciao, {firstName}!</h1>
          <p style={{ fontSize: 13, color: 'var(--text-2)' }}>{user?.business_name}</p>
        </div>
        <div className="avatar">{initials}</div>
      </div>

      {/* Setup checklist */}
      {!isSetupComplete && (
        <div className="setup-card">
          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)' }}>Completa il setup</p>

          <div className={`check-item${socialConnected ? ' done' : ''}`}>
            <div className={`check-icon-wrap${socialConnected ? ' done' : ' pending'}`}>
              {socialConnected
                ? <span className="mi mi-16">check</span>
                : <span className="mi mi-16" style={{ color: 'var(--text-3)' }}>link</span>}
            </div>
            <div className="check-content">
              <div className="check-label-text">Collega i tuoi social</div>
              <div className="check-sub-text">
                {socialConnected ? 'Account collegato correttamente' : 'Necessario per pubblicare'}
              </div>
            </div>
            {!socialConnected && (
              <Link to="/connect" className="check-action-btn">Collega</Link>
            )}
          </div>

          <div className={`check-item${planActive ? ' done' : ''}`}>
            <div className={`check-icon-wrap${planActive ? ' done' : ' pending'}`}>
              {planActive
                ? <span className="mi mi-16">check</span>
                : <span className="mi mi-16" style={{ color: 'var(--text-3)' }}>bolt</span>}
            </div>
            <div className="check-content">
              <div className="check-label-text">Scegli un piano</div>
              <div className="check-sub-text">
                {planActive ? `${planLabel} · Attivo` : 'Attiva un abbonamento per pubblicare'}
              </div>
            </div>
            {!planActive && (
              <Link to="/plans" className="check-action-btn">Scegli</Link>
            )}
          </div>
        </div>
      )}

      {!isSetupComplete && (
        <div className="alert info">
          <span className="mi mi-18">info</span>
          <span>Completa i passaggi sopra per iniziare a pubblicare i tuoi contenuti.</span>
        </div>
      )}

      {/* Dashboard (setup completo) */}
      {isSetupComplete && (
        <>
          {/* Stats */}
          <div className="stat-row">
            <div className="stat-card">
              <div className="stat-icon" style={{ background: 'var(--primary-sub)' }}>
                <span className="mi mi-18" style={{ color: 'var(--primary)' }}>bolt</span>
              </div>
              <div className="stat-value" style={{ fontSize: 16 }}>{planLabel}</div>
              <div className="stat-label">Piano attivo</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon" style={{ background: 'var(--success-sub)' }}>
                <span className="mi mi-18" style={{ color: 'var(--success)' }}>grid_on</span>
              </div>
              <div className="stat-value">{posts.filter(p => p.status === 'published').length}</div>
              <div className="stat-label">Pubblicati</div>
            </div>
          </div>

          {/* Connessioni */}
          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span className="section-label">Connessioni</span>
            {metaStatus?.connected ? (
              <>
                {metaStatus.facebook_page && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 18, fontWeight: 900, color: '#1877F2', width: 24, textAlign: 'center' }}>f</span>
                      <span style={{ fontSize: 13, fontWeight: 500, flex: 1 }}>{metaStatus.facebook_page}</span>
                      <span className="mi mi-18" style={{ color: 'var(--success)' }}>check_circle</span>
                    </div>
                    {metaStatus.instagram_username && <div style={{ height: 1, background: 'var(--border)' }} />}
                  </>
                )}
                {metaStatus.instagram_username && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span className="mi mi-18" style={{ color: '#E1306C', width: 24, textAlign: 'center' }}>photo_camera</span>
                    <span style={{ fontSize: 13, fontWeight: 500, flex: 1 }}>@{metaStatus.instagram_username}</span>
                    <span className="mi mi-18" style={{ color: 'var(--success)' }}>check_circle</span>
                  </div>
                )}
              </>
            ) : (
              <Link to="/connect" style={{ display: 'flex', alignItems: 'center', fontSize: 14, color: 'var(--primary)', fontWeight: 500, textDecoration: 'none' }}>
                <span style={{ flex: 1 }}>Collega i tuoi social</span>
                <span className="mi mi-20">arrow_forward_ios</span>
              </Link>
            )}
          </div>

          {/* CTA */}
          <Link to="/create-post" className="btn btn-primary" style={{ justifyContent: 'center' }}>
            <span className="mi mi-20">add_photo_alternate</span> Pubblica contenuto
          </Link>

          {/* Ultimi post */}
          {posts.length > 0 && (
            <>
              <div className="section-header">
                <h3>Ultimi post</h3>
                <Link to="/posts" style={{ fontSize: 12, color: 'var(--primary)', textDecoration: 'none', fontWeight: 500 }}>Vedi tutti →</Link>
              </div>
              {posts.slice(0, 3).map((p: any) => (
                <div key={p.id} className="post-card">
                  {p.media_url && p.media_type !== 'video' && (
                    <img src={p.media_url} alt="" className="post-thumb" />
                  )}
                  {p.media_url && p.media_type === 'video' && (
                    <div className="post-thumb post-thumb-video">
                      <span className="mi" style={{ fontSize: 32, color: 'var(--text-3)' }}>play_circle_filled</span>
                    </div>
                  )}
                  <div className="post-body">
                    <div className="post-meta">
                      <span className={`post-status ${p.status}`}>
                        <span className="mi mi-16">
                          {p.status === 'published' ? 'check_circle' : p.status === 'publishing' ? 'sync' : 'error'}
                        </span>
                        {statusLabel(p.status)}
                      </span>
                      <span className="post-date">{new Date(p.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}</span>
                    </div>
                    <p className="post-caption">
                      {p.caption?.substring(0, 100)}{(p.caption?.length ?? 0) > 100 ? '…' : ''}
                    </p>
                    <div className="plat-badges">
                      <span className="plat-badge plat-fb">Facebook</span>
                      <span className="plat-badge plat-ig">Instagram</span>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
}
